import { z } from 'zod/v4';
import { FONT_SCHEMA, IFont } from './font.interfaces';
import { EEntityType, ERelationType } from './text-relation-type.enums';
import { ETextType } from './text/text.enums';
import {
  INormalText,
  NORMAL_TEXT_SCHEMA,
  NormalTextId,
  TextId,
  TextLanguageValueId,
} from './text/text.interfaces';
import { Brand } from '../branded-types';
import { LanguagePrefix } from './language.interfaces';

export type CorporateDesignId = Brand<string, 'corporateDesignId'>;

export const CORPORATE_PALETTE_COLOR_SCHEMA = z.object({
  id: z.string(),
  value: z.string().regex(/^[0-9a-fA-F]{6}$/),
  name: z.string(),
  primary: z.boolean(),
});

export interface ICorporatePaletteColor {
  id: string;
  value: string;
  name: string;
  primary: boolean;
}

export const DEFAULT_CORPORATE_COLOR_PALETTE: ICorporatePaletteColor[] = [
  {
    id: 'amcon-blue',
    value: '2D6BA9',
    name: 'AMCON Blau',
    primary: true,
  },
  {
    id: 'amcon-gray',
    value: '4E5256',
    name: 'AMCON Grau',
    primary: false,
  },
  {
    id: 'amcon-black',
    value: '000000',
    name: 'Schwarz',
    primary: false,
  },
  {
    id: 'amcon-white',
    value: 'FFFFFF',
    name: 'Weiß',
    primary: false,
  },
];

export const CORPORATE_DESIGN_SCHEMA: z.ZodType<ICorporateDesign> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as CorporateDesignId)
    .optional(),
  primaryColor: z.string(),
  primaryFontColor: z.string(),
  primaryBorderColor: z.string(),
  primaryAlternativeColor: z.string(),
  primaryDarkColor: z.string(),
  primaryDarkFontColor: z.string(),
  primaryDarkBorderColor: z.string(),
  primaryDarkAlternativeColor: z.string(),
  secondaryColor: z.string(),
  secondaryFontColor: z.string(),
  secondaryBorderColor: z.string(),
  secondaryAlternativeColor: z.string(),
  secondaryDarkColor: z.string(),
  secondaryDarkFontColor: z.string(),
  secondaryDarkBorderColor: z.string(),
  secondaryDarkAlternativeColor: z.string(),
  linkIcon: z.string(),
  linkIconDark: z.string(),
  fontSizeTitle: z.number(),
  fontSizeText: z.number(),
  fontSizeSmall: z.number(),
  font: FONT_SCHEMA.nullable().default(null),
  tabs: z.array(NORMAL_TEXT_SCHEMA),
  buttonBorderRounding: z.number(),
  colorPalette: z
    .array(CORPORATE_PALETTE_COLOR_SCHEMA)
    .default(DEFAULT_CORPORATE_COLOR_PALETTE),
  primaryColorName: z.string().default('Individuell'),
  faviconFilename: z.string(),
  faviconFileExtension: z.string(),
  colorAdmixtures: z.object({
    standard_background: z.string(),
    standard_text: z.string(),
    standard_text_high: z.string(),
    standard_text_disabled: z.string(),
    standard_text_outlined: z.string(),
    standard_text_divider: z.string(),
    error_red: z.string(),
    error_red_dark: z.string(),
    standard_success: z.string(),
    dark_background: z.string(),
    dark_surface_01: z.string(),
    dark_surface_02: z.string(),
    dark_surface_03: z.string(),
    dark_surface_04: z.string(),
    dark_surface_08: z.string(),
    dark_surface_end: z.string(),
    dark_text: z.string(),
    dark_text_high: z.string(),
    dark_text_disabled: z.string(),
    dark_primary: z.string(),
    dark_primary_text: z.string(),
  }),
});

export interface ICorporateDesign {
  id?: CorporateDesignId;
  primaryColor: string;
  primaryFontColor: string;
  primaryBorderColor: string;
  primaryAlternativeColor: string;
  primaryDarkColor: string;
  primaryDarkFontColor: string;
  primaryDarkBorderColor: string;
  primaryDarkAlternativeColor: string;
  secondaryColor: string;
  secondaryFontColor: string;
  secondaryBorderColor: string;
  secondaryAlternativeColor: string;
  secondaryDarkColor: string;
  secondaryDarkFontColor: string;
  secondaryDarkBorderColor: string;
  secondaryDarkAlternativeColor: string;
  linkIcon: string;
  linkIconDark: string;
  fontSizeTitle: number;
  fontSizeText: number;
  fontSizeSmall: number;
  font: IFont | null;
  tabs: INormalText[];
  buttonBorderRounding: number;
  colorPalette: ICorporatePaletteColor[];
  primaryColorName: string;
  faviconFilename: string;
  faviconFileExtension: string;
  colorAdmixtures: {
    standard_background: string;
    standard_text: string;
    standard_text_high: string;
    standard_text_disabled: string;
    standard_text_outlined: string;
    standard_text_divider: string;
    error_red: string;
    error_red_dark: string;
    standard_success: string;
    dark_background: string;
    dark_surface_01: string;
    dark_surface_02: string;
    dark_surface_03: string;
    dark_surface_04: string;
    dark_surface_08: string;
    dark_surface_end: string;
    dark_text: string;
    dark_text_high: string;
    dark_text_disabled: string;
    dark_primary: string;
    dark_primary_text: string;
  };
}

export const DEFAULT_CORPORATE_DESIGN: ICorporateDesign = {
  id: '6356184f-2af8-47a7-b616-45fde85378e7' as CorporateDesignId,
  linkIcon: '#FBBE01',
  linkIconDark: '#FCD14D',
  primaryColor: '#2D6BA9',
  primaryFontColor: '#FFFFFF',
  primaryBorderColor: '#2D6BA9',
  primaryAlternativeColor: '#2D6BA9',
  primaryDarkColor: '#657096',
  primaryDarkFontColor: '#121212',
  primaryDarkBorderColor: '#769FC7',
  primaryDarkAlternativeColor: '#769FC7',
  secondaryColor: '#74BC47',
  secondaryFontColor: '#333333',
  secondaryBorderColor: '#46722C',
  secondaryAlternativeColor: '#333333',
  secondaryDarkColor: '#74BC47',
  secondaryDarkFontColor: '#333333',
  secondaryDarkBorderColor: '#46722C',
  secondaryDarkAlternativeColor: '#74BC47',
  buttonBorderRounding: 6,
  colorPalette: DEFAULT_CORPORATE_COLOR_PALETTE,
  primaryColorName: 'AMCON Blau',
  faviconFileExtension: 'ico',
  faviconFilename: 'favicon',
  font: null,
  fontSizeSmall: 14,
  fontSizeText: 16,
  fontSizeTitle: 32,
  tabs: [
    {
      id: '723dfeb5-f59e-4b56-b1e0-96927fd30287' as NormalTextId,
      text: {
        id: '8843d10a-cdcd-44f3-8c3c-96f3ada9651b' as TextId,
        values: [
          {
            id: '9f7ce770-f3f0-4395-8567-6c041b9f450f' as TextLanguageValueId,
            value: 'Webshop',
            languagePrefix: 'DE' as LanguagePrefix,
          },
        ],
      },
      textType: ETextType.Web,
      relationType: ERelationType.tab,
      entityType: EEntityType.corporateDesign,
    },
  ],
  colorAdmixtures: {
    standard_background: '#FFFFFF',
    standard_text: '#4B4E55',
    standard_text_high: '#333333',
    standard_text_disabled: '#7A7A7A',
    standard_text_outlined: '#C4C4CF',
    standard_text_divider: '#EEEEF1',
    error_red: '#BA1A1A',
    error_red_dark: '#D67676',
    standard_success: '#168721',
    dark_background: '#121212',
    dark_surface_01: '#1E1E1E',
    dark_surface_02: '#232323',
    dark_surface_03: '#252525',
    dark_surface_04: '#272727',
    dark_surface_08: '#2F2F2F',
    dark_surface_end: '#383838',
    dark_text: '#FFFFFF',
    dark_text_high: '#FFFFFF',
    dark_text_disabled: '#FFFFFF',
    dark_primary: '#657096',
    dark_primary_text: '#FFFFFF',
  },
};
